import { useEffect, type FC } from 'react'

import { Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import Highlight from '@/components/Highlight'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearch, useSortParams } from '@/hooks/searchParams'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useRenderLimit } from '@/hooks/use-render-limit'

import { useReportsContext } from '../index.context'

import adminQueries from '../../queries'
import type { ReportFilters, SalaryDeductionDeviceReport } from '../../resources'
import adminResources from '../../resources'

export const SalaryDeductionDevice: FC<{
  filters: ReportFilters
}> = ({ filters }) => {
  const { searchFilter } = useSearch()
  const { isReportListLoading, setCount, setIsReportListLoading } = useReportsContext()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof reportsFiltered)[number]>()

  const stringMatcher = (item: SalaryDeductionDeviceReport) => [
    item.name,
    item.model,
    item.isBuyout,
    item.coverAmount,
    item.totalAmount,
  ]

  const { data: reports = [] } = useQuery(
    adminQueries.adminSalaryDeductionDeviceReports(filters),
    async () => {
      const reports = await adminResources.getSalaryDeductionDeviceReports(filters)
      setIsReportListLoading(false)
      return reports
    },
    { enabled: Boolean(filters.fromDate && filters.toDate) }
  )

  useEffect(() => {
    setCount(reports.length)
  }, [reports, setCount])

  const reportsFiltered = reports.filter(reportsObject =>
    getQueryFilter(searchFilter, stringMatcher)(reportsObject)
  )
  const sortedList = getSortedList({
    list: reportsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)

  const total = reportsFiltered.length
  const { formatNumber } = useFormattedNumberWithCurrency()
  const translate = useTranslation()

  return (
    <>
      <ResultCount count={total} />
      {total > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th onClick={handleSort} id="name">
                <Translation id="Name" />
              </Th>
              <Th onClick={handleSort} id="model">
                <Translation id="Model" />
              </Th>
              <Th onClick={handleSort} id="isBuyout">
                <Translation id="Is buyout" />
              </Th>

              <Th onClick={handleSort} id="downPayments" className="text-right">
                <Translation id="Deduction Months" />
              </Th>
              <Th onClick={handleSort} id="downPayments" className="text-right">
                <Translation id="Deduction This Period" />
              </Th>
              <Th onClick={handleSort} isSortable={false} id="totalAmount" className="text-right">
                <div>
                  <Translation id="Cover Amount" />
                </div>
                <div className="text-sm text-gray-400">
                  <Translation id="Total Amount" />
                </div>
              </Th>
            </tr>
          </thead>
          <tbody>
            {visibleList?.map(
              (
                {
                  name,
                  employeeNo,
                  model,
                  isBuyout,
                  departmentCode,
                  coverAmount,
                  totalAmount,
                  downPayments,
                  amount,
                },
                index
              ) => (
                <tr key={`${departmentCode}-${index}`}>
                  <Td>
                    <div>
                      <Highlight text={name} pattern={searchFilter} />
                    </div>

                    <div className="text-sm text-gray-400">
                      <Highlight pattern={searchFilter} text={employeeNo || ''} />
                    </div>
                  </Td>
                  <Td>
                    <Highlight text={model} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={isBuyout ? translate('Yes') : 'No'} pattern={searchFilter} />
                  </Td>
                  <Td className="text-right">
                    <Highlight text={downPayments.toString()} pattern={searchFilter} />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(amount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <div>
                      <Highlight
                        text={formatNumber({ amount: Number(coverAmount), precision: 2 })}
                        pattern={searchFilter}
                      />
                    </div>
                    <div className="text-sm text-gray-400">
                      <Highlight
                        text={formatNumber({ amount: Number(totalAmount), precision: 2 })}
                        pattern={searchFilter}
                      />
                    </div>
                  </Td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      ) : (
        !isReportListLoading && <NoResults />
      )}
      <ListLoaderComponent />
    </>
  )
}
